import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParallaxHero from '../components/ParallaxHero';
import { 
  BookOpen, Scale, Target, TrendingUp, FileText, Users, Download, ExternalLink, Calendar, ArrowRight, CheckCircle 
} from 'lucide-react';

const CSRLandscapePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('policy');
  const [contentVisible, setContentVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  
  const contentRef = useRef(null);
  const ctaRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.15, rootMargin: '0px 0px -50px 0px' };
    
    const createObserver = (setVisible) => new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, observerOptions);

    const contentObserver = createObserver(setContentVisible);
    const ctaObserver = createObserver(setCtaVisible);

    if (contentRef.current) contentObserver.observe(contentRef.current);
    if (ctaRef.current) ctaObserver.observe(ctaRef.current);

    return () => {
      contentObserver.disconnect();
      ctaObserver.disconnect();
    };
  }, []);

  // Warm Premium Palette - Consistent with Homepage
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4A7D78',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    bgWarmWhite: '#FFFDFB',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
    borderSage: '#C5D5CF',
    success: '#4D9078',
  };

  const latestUpdates = [
    { date: "15 Jan 2025", title: "MCA Circular: New CSR Reporting Guidelines", type: "Policy Update" },
    { date: "10 Jan 2025", title: "Revised Schedule VII Activities - What Changed", type: "Regulatory" },
    { date: "05 Jan 2025", title: "FY 2024-25: INR 25,000 Cr CSR Spend Reported", type: "Data Insight" }
  ];

  const policyFramework = [
    { heading: "Companies Act 2013 - Section 135", description: "Mandates CSR spending for companies with net worth ≥ ₹500 Cr, turnover ≥ ₹1,000 Cr, or net profit ≥ ₹5 Cr.", icon: Scale },
    { heading: "Schedule VII Activities", description: "Lists permissible CSR activities including education, healthcare, environmental sustainability, and rural development.", icon: BookOpen },
    { heading: "CSR Rules & Amendments", description: "Regular updates from MCA including impact assessment requirements, CSR committee composition, and reporting formats.", icon: FileText }
  ];

  const governmentMissions = [
    { title: "Swachh Bharat Mission", sector: "Sanitation & Hygiene", description: "Nation-wide campaign for cleanliness and waste management." },
    { title: "Skill India Mission", sector: "Education & Skilling", description: "Upskilling 40 crore Indians by 2030 through vocational training." },
    { title: "Ayushman Bharat", sector: "Healthcare", description: "Universal health coverage initiative supporting health centers." },
    { title: "Digital India", sector: "Technology & Access", description: "Bridging the digital divide through literacy and connectivity." }
  ];

  const tabs = [
    { id: 'policy', label: 'Policy Framework' },
    { id: 'missions', label: 'Govt. Missions' },
    { id: 'updates', label: 'Latest Updates' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />

      {/* Hero Section - Background image like homepage */}
      <section 
        className="relative min-h-[70vh] overflow-hidden"
        data-testid="csr-hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ecoconsu/artifacts/47igvlj4_image.png)',
            backgroundPosition: 'center center',
          }}
        />
        
        {/* Gradient overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, 
              rgba(44, 24, 16, 0.7) 0%, 
              rgba(61, 107, 103, 0.5) 40%, 
              rgba(61, 107, 103, 0.3) 65%,
              rgba(61, 107, 103, 0.15) 100%
            )`
          }}
        />
        
        {/* Content - Vertically centered */}
        <div className="relative z-10 min-h-[70vh] flex items-center">
          <div className="w-full py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
              
              {/* Hero Content */}
              <div className="max-w-xl lg:max-w-2xl">
                
                {/* Small badge */}
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 opacity-0 animate-fade-in-up"
                  style={{ 
                    animationDelay: '0.1s', 
                    animationFillMode: 'forwards',
                    backgroundColor: 'rgba(77, 144, 120, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.25)'
                  }}
                >
                  <BookOpen className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                  <span 
                    className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: '#FFFFFF' }}
                  >
                    Knowledge Center
                  </span>
                </div>
                
                {/* Main Heading */}
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] tracking-tight opacity-0 animate-fade-in-up"
                  style={{ 
                    color: '#FFFFFF',
                    textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 2px 10px rgba(0,0,0,0.3)',
                    animationDelay: '0.2s', 
                    animationFillMode: 'forwards'
                  }}
                  data-testid="csr-hero-title"
                >
                  India&apos;s CSR<br />Landscape
                </h1>

                {/* Subheading */}
                <p 
                  className="text-lg md:text-xl mb-8 leading-relaxed max-w-lg opacity-0 animate-fade-in-up"
                  style={{ 
                    color: 'rgba(255, 255, 255, 0.95)',
                    textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                    animationDelay: '0.3s', 
                    animationFillMode: 'forwards'
                  }}
                >
                  Your definitive guide to Corporate Social Responsibility — laws, policies, missions, and best practices.
                </p>

                {/* Last updated badge */}
                <div 
                  className="inline-flex items-center gap-2 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
                >
                  <Calendar className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.8)' }} />
                  <span 
                    className="text-sm font-medium"
                    style={{ color: 'rgba(255,255,255,0.8)', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}
                  >
                    Last updated: January 2025
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-6" style={{ backgroundColor: colors.bgSage, borderBottom: `1px solid ${colors.borderSage}` }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  backgroundColor: activeTab === tab.id ? colors.primaryTeal : 'transparent',
                  color: activeTab === tab.id ? colors.textOnDark : colors.textPrimary,
                  boxShadow: activeTab === tab.id ? '0 4px 14px rgba(61, 107, 103, 0.25)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = colors.bgCard;
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(44, 24, 16, 0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section ref={contentRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.bgCream }}>
        <div className="max-w-6xl mx-auto">
          
          {/* Policy Framework */}
          {activeTab === 'policy' && (
            <div>
              <div 
                className={`text-center mb-14 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                  Policy Framework
                </h2>
                <p className="mt-4 max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
                  Understanding the regulatory landscape that governs CSR in India.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {policyFramework.map((item, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl p-8 transition-all duration-500 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                    style={{ 
                      backgroundColor: colors.bgCard, 
                      border: `1px solid ${colors.borderWarm}`,
                      transitionDelay: contentVisible ? `${200 + idx * 150}ms` : '0ms'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = colors.primaryTeal;
                      e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.primaryTeal}, 0 12px 24px rgba(61, 107, 103, 0.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = colors.borderWarm;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                      style={{ backgroundColor: colors.bgSage }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: colors.primaryTeal }} />
                    </div>
                    <h3 className="text-lg font-semibold mb-3" style={{ color: colors.textPrimary }}>{item.heading}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Government Missions */}
          {activeTab === 'missions' && (
            <div>
              <div 
                className={`text-center mb-14 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                  Government Missions
                </h2>
                <p className="mt-4 max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
                  Key national initiatives aligned with CSR objectives.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {governmentMissions.map((mission, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl p-8 transition-all duration-500 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                    style={{ 
                      backgroundColor: colors.bgCard, 
                      border: `1px solid ${colors.borderWarm}`,
                      transitionDelay: contentVisible ? `${200 + idx * 100}ms` : '0ms'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = colors.primaryTeal;
                      e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.primaryTeal}, 0 8px 24px rgba(61, 107, 103, 0.08)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = colors.borderWarm;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{mission.title}</h3>
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: colors.bgSage, color: colors.primaryTeal }}
                      >
                        {mission.sector}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>{mission.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Latest Updates */}
          {activeTab === 'updates' && (
            <div>
              <div 
                className={`text-center mb-14 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                  Latest Updates
                </h2>
                <p className="mt-4 max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
                  Stay informed with the latest CSR news and regulatory changes.
                </p>
              </div>
              <div className="space-y-4 max-w-3xl mx-auto">
                {latestUpdates.map((update, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl p-6 flex items-center gap-6 transition-all duration-500 cursor-pointer ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ 
                      backgroundColor: colors.bgCard, 
                      border: `1px solid ${colors.borderWarm}`,
                      transitionDelay: contentVisible ? `${200 + idx * 100}ms` : '0ms'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(8px)';
                      e.currentTarget.style.borderColor = colors.primaryTeal;
                      e.currentTarget.style.boxShadow = `0 4px 16px rgba(61, 107, 103, 0.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.borderColor = colors.borderWarm;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="text-center flex-shrink-0" style={{ minWidth: '80px' }}>
                      <p className="text-sm font-semibold" style={{ color: colors.primaryTeal }}>{update.date}</p>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold mb-1" style={{ color: colors.textPrimary }}>{update.title}</p>
                      <span 
                        className="text-xs font-medium px-2 py-1 rounded"
                        style={{ backgroundColor: colors.bgSage, color: colors.textSecondary }}
                      >
                        {update.type}
                      </span>
                    </div>
                    <ExternalLink className="w-5 h-5 flex-shrink-0" style={{ color: colors.textTertiary }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-20 px-6" style={{ backgroundColor: colors.primaryTeal }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className={`text-3xl md:text-4xl font-bold mb-6 tracking-tight transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ color: colors.textOnDark }}
          >
            Need Expert Guidance?
          </h2>
          <p 
            className={`text-lg mb-10 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ color: colors.textOnDarkMuted, transitionDelay: ctaVisible ? '150ms' : '0ms' }}
          >
            Our team can help you navigate CSR compliance and create impactful programs.
          </p>
          <button 
            onClick={() => navigate('/governance')}
            className={`inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ 
              backgroundColor: colors.primaryTeal, 
              color: colors.textOnDark, 
              boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)',
              transitionDelay: ctaVisible ? '300ms' : '0ms'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryTealLight}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primaryTeal}
            data-testid="csr-cta-btn"
          >
            Explore Governance Suite
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CSRLandscapePage;
